import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { flatten } from 'lodash';
import { useTranslation } from 'react-i18next';
import useSearchParams from '../../../utils/hooks/useSearchParams';

import StaticContext from './StaticContext';
import { COMPONENT_NAME_SITE_LOGO, DISPLAY_NAME_LANGUAGE_MENU } from '../../../utils/constants';
import { LANGUAGE_URL } from './constants';
import { createObjectFromProps } from '../../../utils/object';
import { reduceItems, useConfiguration } from './utils';
import { useLanguage, useLanguageSelectors, useRoute } from '../../../utils/sitecoreContext';

const StaticProvider = ({ children, appNameConfig, fields, componentName, primaryItems: propPrimaryItems }) => {
  const { t } = useTranslation();
  const config = useConfiguration(appNameConfig);
  const gtmTags = { type: componentName };
  const searchParams = useSearchParams();

  const { items } = createObjectFromProps(fields);
  const { primaryItems, leftPrimaryItems, rightPrimaryItems, secondaryItems } = reduceItems(items, config);

  const language = useLanguage();
  const languageSelectors = useLanguageSelectors();
  const route = useRoute();

  const siteLogo = useMemo(
    () =>
      createObjectFromProps(
        flatten(Object.values(route?.placeholders)).find(
          component => component?.componentName === COMPONENT_NAME_SITE_LOGO,
        ),
      ),
    [],
  );

  const languageMenu = useMemo(
    () => ({
      itemId: DISPLAY_NAME_LANGUAGE_MENU,
      itemUrl: LANGUAGE_URL,
      itemName: DISPLAY_NAME_LANGUAGE_MENU,
      itemDisplayName: DISPLAY_NAME_LANGUAGE_MENU,
      current: language,
      items: [
        {
          items: languageSelectors?.map(item => ({
            itemId: item.code,
            itemUrl: LANGUAGE_URL,
            itemName: item.code,
            itemDisplayName: item.code,
            label: t(`Shared.Header.${[item.code]}`),
            url: { href: searchParams.size ? `${item.url}?${searchParams.toString()}` : item.url },
          })),
        },
      ],
      label: `${t('Shared.Header.language')}: ${language?.toUpperCase()}`,
    }),
    [language, languageSelectors, searchParams],
  );

  const contextValue = useMemo(
    () => ({
      config,
      gtmTags,
      languageMenu,
      leftPrimaryItems,
      primaryItems: propPrimaryItems ?? primaryItems,
      rightPrimaryItems,
      secondaryItems,
      siteLogo,
    }),
    [config, appNameConfig, language, languageMenu],
  );

  return <StaticContext.Provider value={contextValue}>{children}</StaticContext.Provider>;
};

StaticProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

export default StaticProvider;
